import { useMemo, useState } from "react";
import {
  useGetCategoriesQuery,
  useDeleteCategoryMutation,
} from "../../../../redux/api";
import type { Category } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { CategoryFormModal } from "./category";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 5;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Inventory → Categories (list page).
const Categories = () => {
  const {
    data: categories = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCategoriesQuery();

  const [deleteCategory] = useDeleteCategoryMutation();

  const [search, setSearch] = useState("");

  const nameById = (id?: string | null) =>
    id ? categories.find((c) => c.id === id)?.name ?? id : "—";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = categories.find((c) => c.id === viewingId) ?? null;


  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Delete "${category.name}"?`)) return;
    try {
      await deleteCategory(category.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete category.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c: Category) =>
      [c.name, c.code, c.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [categories, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load categories"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Categories"
          onBack={() => setViewingId(null)}
          title={viewing.name}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Code", value: viewing.code },
              { label: "Parent category", value: nameById(viewing.parentId) },
              { label: "Description", value: viewing.description, full: true },
            ]}
          />
        </EntityOverview>
        <CategoryFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Categories</h2>
            <p className="panel__sub">
              Showing {visible.length} of {categories.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New category
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Parent</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading categories…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {categories.length === 0
                      ? "No categories yet."
                      : "No categories match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((category: Category) => (
                  <tr key={category.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(category.id)}
                      >
                        {category.name}
                      </button>
                    </td>
                    <td>
                      {category.code ? (
                        <span className="pill pill--neutral">{category.code}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{nameById(category.parentId)}</td>
                    <td>
                      <span className={`badge ${statusTone(category.status)}`}>
                        <span className="badge__dot" />
                        {category.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View category",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(category.id),
                          },
                          {
                            label: "Edit category",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(category.id),
                          },
                          {
                            label: "Delete category",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(category),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <CategoryFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Categories;
